import { Button, Form, Input, Modal, Radio, Select, Tag, Tabs, message } from "antd";
import DatePicker from "../../../../../components/inputs/MomentDatePicker";
import axios from "axios";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { BiSearch } from "react-icons/bi";
import { useQuery, useQueryClient } from "react-query";
import { useSelector } from "react-redux";
import { serverUrl } from "../../../../../nestserver";
import { selectSchoolId } from "../../../../../redux/slices/auth.slice";
import TemplateListing from "./FeeListing";
import FeeTable from "./FeeTable";
import TransportBillingZoneManager from "./TransportBillingZoneManager";

const FeeTemplate = () => {
  const schoolId = useSelector(selectSchoolId);
  const { data: schoolData } = useQuery(
    ["school", schoolId],
    () => axios.get(`${serverUrl}/school/${schoolId}`, {
      headers: { Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}` },
    }).then((r) => r.data),
    { enabled: !!schoolId, staleTime: 5 * 60 * 1000 },
  );
  const isMontessoriEnabled = schoolData?.result?.data?.is_montessori_enabled === true;
  const isManualMode = schoolData?.result?.data?.is_manual_fee_assignment_enabled === true;

  const getCurrentAcademicYear = () => {
    const today = new Date();
    const curYear = today.getFullYear();
    const curMonth = today.getMonth() + 1;
    const startYear = curMonth >= 6 ? curYear : curYear - 1;
    const endYearShort = String(startYear + 1).slice(-2);
    return `${startYear}-${endYearShort}`;
  };

  const academicYears = [getCurrentAcademicYear()];

  const { Search } = Input;
  const queryClient = useQueryClient();
  const { Option } = Select;
  const [form] = Form.useForm();
  const [addTemplate, setAddTemplate] = useState(false);
  const [otherFields, setOtherFields] = useState([]);
  const [feeComponents, setFeeComponents] = useState();
  const [templateType, setTemplateType] = useState();
  const [postTemplateResponse, setPostTemplateResponse] = useState();
  const [selectedZoneId, setSelectedZoneId] = useState(null);
  const [selectedZoneFee, setSelectedZoneFee] = useState("");
  const [paymentMode, setPaymentMode] = useState("termly");
  const [selectedYear, setSelectedYear] = useState(getCurrentAcademicYear());

  const computeInstallmentCount = (mode, startDate, endDate) => {
    if (mode === "yearly") return 1;
    if (mode === "quarterly") return 4;
    if (mode === "monthly") {
      if (!startDate || !endDate) return 12;
      const start = moment.isMoment(startDate) ? startDate : moment(startDate);
      const end = moment.isMoment(endDate) ? endDate : moment(endDate);
      return Math.max(1, Math.min(12, Math.ceil(end.diff(start, "months", true))));
    }
    return null;
  };

  const paymentModeLabel = (mode, startDate, endDate) => {
    if (mode === "yearly") return "1 installment — full year bulk payment";
    if (mode === "quarterly") return "4 installments — every quarter";
    if (mode === "monthly") {
      const count = computeInstallmentCount("monthly", startDate, endDate);
      return `${count} installment${count !== 1 ? "s" : ""} — one per month`;
    }
    return null;
  };
  const [selectedYearr, setSelectedYearr] = useState(null);
  const [academicStartDate, setAcademicStartDate] = useState(null);
  const [academicEndDate, setAcademicEndDate] = useState(null);
  const [zoneManagerVisible, setZoneManagerVisible] = useState(false);

  const [data, setData] = useState({
    tution: "",
  });
  const [categories, setCategories] = useState([]);

  const [searchQuery, setSearchQuery] = useState("");

  const addOtherField = () => {
    setOtherFields([
      ...otherFields,
      { id: Date.now(), label: "", value: "" },
    ]);
  };

  const removeOtherField = (id) => {
    setOtherFields((prevFields) => prevFields.filter((item) => item.id !== id));
  };


  const handleFeeData = (paramName, value) => {
    setData({ ...data, [paramName]: value });
  };

  const handleOtherFieldChange = (value, id, field) => {
    setOtherFields((prevFields) =>
      prevFields.map((item) =>
        item.id === id ? { ...item, [field]: value } : item
      )
    );
  };

  const fetchClasses = async () => {
    const res = await axios({
      method: "get",
      url: serverUrl + "/classes",
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
      },
    });
    return res.data;
  };
  const classes = useQuery("classes", fetchClasses);

  const calculateTotal = () => {
    const other = parseFloat(data.other) || 0;
    const otherFieldsTotal = otherFields.reduce((total, field) => {
      const value = parseFloat(field.value) || 0;
      return total + value;
    }, 0);
    // When categories are defined, use first category's amount as reference tuition
    const tution = categories.length > 0
      ? (parseFloat(categories[0]?.tuition_amount) || 0)
      : (parseFloat(data.tution) || 0);

    const total = tution + other + otherFieldsTotal;

    if (total === 0) {
      form.setFieldsValue({ total: "" });
    } else {
      form.setFieldsValue({ total: total.toFixed(2) });
    }
  };

  useEffect(() => {
    calculateTotal();
    if (data) {
      const concatenatedData = Object.entries(data).map(([key, value]) => ({
        label: key,
        value,
      }));
      const mergedFields = [...concatenatedData, ...otherFields];
      setFeeComponents(mergedFields);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otherFields, data, categories, templateType]);

  async function fetchTemplateData(type, selectedYear, search) {
    const res = await axios({
      method: "get",
      url: `${serverUrl}/admin/fee-template/list`,
      params: {
        type,
        year: selectedYear,
        search,
      },
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
      },
    });
    return res?.data?.result;
  }

  // React Query hooks for fetching data
  const { data: tutionData } = useQuery(
    ["fee-template", "tution", selectedYear, searchQuery],
    () => fetchTemplateData("tution", selectedYear, searchQuery)
  );

  const { data: accommodationData } = useQuery(
    ["fee-template", "accommodation", selectedYear, searchQuery],
    () => fetchTemplateData("accommodation", selectedYear, searchQuery)
  );
  const { data: transportationData } = useQuery(
    ["fee-template", "transportation", selectedYear, searchQuery],
    () => fetchTemplateData("transportation", selectedYear, searchQuery)
  );
  const { data: daycareData } = useQuery(
    ["fee-template", "daycare", selectedYear, searchQuery],
    () => fetchTemplateData("daycare", selectedYear, searchQuery)
  );

  const billingZones = useQuery(["transport-billing-zones", "new"], async () => {
    const res = await axios.get(
      `${serverUrl}/admin/transport-billing-zone/list?excludeApplied=true`,
      { headers: { Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}` } }
    );
    return res.data?.result || [];
  });

  const handleZoneSelect = (zoneId) => {
    setSelectedZoneId(zoneId);
    const zone = (billingZones.data || []).find((z) => z._id === zoneId);
    const firstDestAmount = zone?.destinations?.[0]?.amount;
    setSelectedZoneFee(zone ? String(firstDestAmount ?? zone.fee_amount ?? "") : "");
  };

  function disabledDate(current) {
    if (!current) return false;
    let effectiveStart = academicStartDate ? moment(academicStartDate) : null;
    let effectiveEnd = academicEndDate ? moment(academicEndDate) : null;
    // Derive from selected year when class-based dates are unavailable (e.g. transportation)
    if (!effectiveStart && selectedYear) {
      const startYr = parseInt(selectedYear.split("-")[0]);
      if (!isNaN(startYr)) {
        effectiveStart = moment(`${startYr}-06-01`);
        if (!effectiveEnd) effectiveEnd = moment(`${startYr + 1}-05-31`);
      }
    }
    if (effectiveStart && current < moment(effectiveStart).startOf("day")) return true;
    if (effectiveEnd && current > moment(effectiveEnd).endOf("day")) return true;
    return false;
  }

  const onFinish = (values) => {
    let templatePayload;

    const resolvedInstallmentCount =
      paymentMode === "termly"
        ? values.installment_count
        : computeInstallmentCount(
            paymentMode,
            academicStartDate,
            academicEndDate
          );

    if (templateType === "transportation") {
      if (!selectedZoneId) {
        message.error("Please select a billing zone.");
        return;
      }
      const feeAmt = parseFloat(selectedZoneFee);
      if (!feeAmt || feeAmt <= 0) {
        message.error("Please enter a valid fee amount for the selected zone.");
        return;
      }
      templatePayload = {
        type: templateType,
        title: values?.title,
        fee_components: [],
        destination_fees: [{ destination_id: selectedZoneId, fee_amount: feeAmt }],
        due_date: values?.due_date?.format("YYYY-MM-DD"),
        academic_start_date: academicStartDate ? moment(academicStartDate).format("YYYY-MM-DD") : null,
        academic_end_date: academicEndDate ? moment(academicEndDate).format("YYYY-MM-DD") : null,
        year: selectedYear,
        installment_count: resolvedInstallmentCount,
        payment_mode: paymentMode,
        discount: 0,
      };
    } else {
      const computedTotal = parseFloat(form.getFieldValue("total"));
      if (!computedTotal || computedTotal <= 0) {
        message.error("Total fee amount must be greater than zero.");
        return;
      }
      const validCategories = categories.filter(
        (c) => c.name && Number(c.tuition_amount) > 0
      );
      // When categories exist, use first category's amount as the reference tution component
      const referenceTution = validCategories.length > 0
        ? Number(validCategories[0].tuition_amount)
        : null;
      const baseComponents = feeComponents.filter(
        (component) =>
          component.label &&
          component.value &&
          parseFloat(component.value) > 0
      );
      const finalComponents = referenceTution != null
        ? [
            { name: "tution", amount: referenceTution },
            ...baseComponents.filter((c) => c.label !== "tution").map((c) => ({
              name: c.label,
              amount: parseFloat(c.value),
            })),
          ]
        : baseComponents.map((component) => ({
            name: component.label,
            amount: parseFloat(component.value),
          }));
      templatePayload = {
        type: templateType,
        class: values?.class,
        title: values?.title,
        fee_components: finalComponents,
        due_date: values?.due_date?.format("YYYY-MM-DD"),
        academic_start_date: academicStartDate ? moment(academicStartDate).format("YYYY-MM-DD") : null,
        academic_end_date: academicEndDate ? moment(academicEndDate).format("YYYY-MM-DD") : null,
        year: selectedYear,
        installment_count: resolvedInstallmentCount,
        payment_mode: paymentMode,
        discount: 0,
        ...(validCategories.length > 0 && {
          categories: validCategories.map((c) => ({
            name: c.name,
            tuition_amount: Number(c.tuition_amount),
          })),
        }),
      };
    }

    axios
      .post(serverUrl + "/admin/fee-template/", templatePayload, {
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
        },
      })
      .then((res) => {
        queryClient.invalidateQueries(["fee-template"]);
        setPostTemplateResponse(res);
        message.success(res?.data?.result?.message);
      })
      .catch((err) => {
        if (err?.response?.data?.error?.errors) {
          message.error(err?.response?.data?.error?.errors);
        }
      });
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${serverUrl}/admin/fee-template/${id}`, {
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
        },
      });
      queryClient.invalidateQueries(["fee-template"]);
      message.success("Template deleted successfully");
    } catch (error) {
      message.error(error?.response?.data?.error?.errors);
    }
  };

  return (
    <>
      <div className="template">
        <div className="pt-8">
          <div className="bg-white p-10 rounded-2xl">
            <div className="text-2xl text-orange-500 font-semibold pb-8">
              Fee
            </div>
            <div className="flex justify-between pb-4">
              <Select
                style={{ width: 150 }}
                value={selectedYear}
                onChange={(val) => setSelectedYear(val)}
              >
                {academicYears.map((yr) => (
                  <Option key={yr} value={yr}>
                    {yr}
                  </Option>
                ))}
              </Select>
              <Search
                placeholder="input search text"
                enterButton="Search"
                size="large"
                loading={false}
                style={{ backgroundColor: "#E3E3E3" }}
                prefix={<BiSearch className="text-gray-600 " />}
                onSearch={(value) => setSearchQuery(value)}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-310"
              />
            </div>

            <Tabs
              defaultActiveKey="tution"
              type="line"
              tabBarGutter={0}
              tabBarStyle={{ borderBottom: "2px solid #f0f0f0", marginBottom: 0 }}
            >
              {[
                { key: "tution",        label: "Tuition",        data: tutionData,         color: "#fa8c16" },
                { key: "transportation",label: "Transportation",  data: transportationData, color: "#1890ff" },
                { key: "accommodation", label: "Accommodation",   data: accommodationData,  color: "#52c41a" },
                ...(isMontessoriEnabled ? [{ key: "daycare", label: "Day Care", data: daycareData, color: "#722ed1" }] : []),
              ].map(({ key, label, data, color }) => (
                <Tabs.TabPane
                  key={key}
                  tab={
                    <div style={{ padding: "10px 28px", textAlign: "center", minWidth: 140 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: "inherit" }}>{label}</div>
                      <div style={{ fontSize: 11, marginTop: 2, color: "#8c8c8c", fontWeight: 400 }}>
                        {data?.length ?? 0} template{data?.length !== 1 ? "s" : ""}
                      </div>
                    </div>
                  }
                >
                  {key === "transportation" && (
                    <div className="flex justify-end px-4 pt-3 pb-1">
                      <Button
                        onClick={() => setZoneManagerVisible(true)}
                        style={{
                          backgroundColor: "#09617E",
                          borderColor: "#09617E",
                          color: "#ffffff",
                          height: 40,
                          paddingInline: 10,
                          fontSize: 14,
                          fontWeight: 600,
                        }}
                      >
                        Van Fee Zones
                      </Button>
                    </div>
                  )}
                  <TemplateListing
                    type={key}
                    filteredData={data}
                    handleDelete={handleDelete}
                    setAddTemplate={setAddTemplate}
                    setTemplateType={setTemplateType}
                  />
                </Tabs.TabPane>
              ))}
            </Tabs>
          </div>
        </div>

        {/* ----------------------------------modal------------------------------------ */}
        {/* Add Template */}
        <Modal
          width={800}
          visible={addTemplate}
          onOk={() => setAddTemplate(false)}
          onCancel={() => {
            form.resetFields();
            setAddTemplate(false);
            setOtherFields([]);
            setCategories([]);
            setPostTemplateResponse(null);
            form.setFieldsValue({ total: "" });
            setFeeComponents();
            setSelectedZoneId(null);
            setSelectedZoneFee("");
            setPaymentMode("termly");
          }}
          footer={null}
          forceRender
        >
          <div>
            <h3 className="text-lg font-bold pb-3">Add Template</h3>
            <div className="pt-4 w-4/5">
              <Form
                form={form}
                name="basic"
                onFinish={onFinish}
                autoComplete="off"
                layout="vertical"
              >
                {templateType !== "transportation" && (
                  <Form.Item
                    label="Class"
                    name="class"
                    className="w-full"
                    rules={[
                      {
                        required: true,
                        message: "Please select a class",
                      },
                    ]}
                  >
                    <Select
                      showSearch
                      showArrow
                      placeholder="Select Class"
                      className="w-full bg-gray-300"
                      style={{
                        border: "6px",
                        backgroundColor: "#EBEBEB",
                      }}
                      getPopupContainer={(trigger) => trigger.parentNode}
                      onChange={(value) => {
                        const selectedClass = classes?.data?.find(
                          (c) => c._id === value
                        );
                        if (selectedClass) {
                          const startMoment = selectedClass.academic_start_date
                            ? moment(selectedClass.academic_start_date)
                            : null;
                          const endMoment = selectedClass.academic_end_date
                            ? moment(selectedClass.academic_end_date)
                            : null;
                          setAcademicStartDate(startMoment);
                          setAcademicEndDate(endMoment);
                          setSelectedYearr(startMoment ? startMoment.year() : null);
                        }
                      }}
                    >
                      {classes?.isSuccess &&
                        classes?.data?.map((data) => {
                          return (
                            <Option key={data._id} value={data._id}>
                              {data.name}
                            </Option>
                          );
                        })}
                    </Select>
                  </Form.Item>
                )}
                <Form.Item
                  label="Title"
                  name="title"
                  className="w-30"
                  rules={[
                    {
                      required: true,
                      message: "Title is required",
                    },
                  ]}
                >
                  <Input placeholder="Enter title" />
                </Form.Item>
                {templateType === "transportation" && (
                  <div className="mb-4">
                    <Form.Item label="Billing Zone" required>
                      {(billingZones.data || []).length === 0 ? (
                        <p className="text-xs text-gray-400">
                          No available billing zones. All zones may already be assigned to an applied template, or none have been configured yet.
                        </p>
                      ) : (
                        <Select
                          showSearch
                          placeholder="Select a billing zone"
                          optionFilterProp="children"
                          value={selectedZoneId}
                          onChange={handleZoneSelect}
                          getPopupContainer={(t) => t.parentNode}
                        >
                          {(billingZones.data || []).map((z) => (
                            <Option key={z._id} value={z._id}>{z.name}</Option>
                          ))}
                        </Select>
                      )}
                    </Form.Item>
                    {selectedZoneId && (() => {
                      const zone = (billingZones.data || []).find((z) => z._id === selectedZoneId);
                      const destinations = zone?.destinations || [];
                      return (
                        <>
                          <Form.Item label="Annual Fee (₹)">
                            <Input
                              type="number"
                              min={0}
                              disabled
                              value={selectedZoneFee}
                              onKeyDown={(e) => {
                                if (e.key === "ArrowUp" || e.key === "ArrowDown")
                                  e.preventDefault();
                              }}
                              onWheel={(e) => e.target.blur()}
                            />
                          </Form.Item>
                          {destinations.length > 0 && (
                            <Form.Item label="Destinations in this Zone">
                              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                                {destinations.map((dest) => (
                                  <Tag key={dest._id} color="blue">
                                    {dest.name} — ₹{Number(dest.amount).toLocaleString("en-IN")}
                                  </Tag>
                                ))}
                              </div>
                            </Form.Item>
                          )}
                        </>
                      );
                    })()}
                  </div>
                )}

                {templateType !== "transportation" && !isManualMode && (
                  <Form.Item
                    name="tution"
                    label="Fee"
                    className="w-30"
                    rules={[
                      {
                        required: true,
                        message: "Fee is required",
                      },
                    ]}
                  >
                    <Input
                      type="number"
                      min={0}
                      onChange={(e) => {
                        handleFeeData("tution", e.target.value);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                          e.preventDefault();
                        }
                      }}
                      onWheel={(e) => e.target.blur()}
                      placeholder="Enter amount"
                    />
                  </Form.Item>
                )}
                {/* {otherFields?.map((field, index) => (
                  <div key={index} className=" space-x-5 mb-2">
                    <h6 key={index} className="mb-8px capitalize">
                      {field?.label}
                    </h6>
                    <div className="flex ml-0px space-x-4 other-feild">
                      <Form.Item
                        name={`otherField_${index}`}
                        label={
                          <Input
                            value={field.label}
                            onChange={(e) => {
                              const value = e.target.value;
                              const capitalizedValue =
                                value.charAt(0).toUpperCase() + value.slice(1);
                              handleOtherFieldChange(
                                capitalizedValue,
                                index,
                                "label"
                              );
                            }}
                            onKeyDown={(e) => {
                              if (
                                e.key === "ArrowUp" ||
                                e.key === "ArrowDown"
                              ) {
                                e.preventDefault();
                              }
                            }}
                            onWheel={(e) => e.target.blur()}
                            placeholder="Enter title"
                            className="w-24 mr-2 flex"
                            rules={[
                              {
                                required: true,
                                message: "Label is required",
                              },
                            ]}
                          />
                        }
                        className="w-30"
                      >
                        <Input
                          onChange={(e) =>
                            handleOtherFieldChange(
                              e.target.value,
                              index,
                              "value"
                            )
                          }
                          onKeyDown={(e) => {
                            if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                              e.preventDefault();
                            }
                          }}
                          onWheel={(e) => e.target.blur()}
                          className="mr-2"
                          placeholder="Enter amount"
                          type="number"
                          min={0}
                        />
                      </Form.Item>
                      <Button
                        type="danger"
                        onClick={() => removeOtherField(index)}
                        className="ml-2"
                      >
                        Remove
                      </Button>
                    </div>
                  </div>
                ))} */}
                {templateType === "tution" && otherFields?.map((field) => (
                  <div key={field.id} className="space-x-5 mb-2">
                    <h6 className="mb-8px capitalize">{field?.label}</h6>
                    <div className="flex items-center ml-0px space-x-4 other-feild">
                      <Input
                        value={field.label}
                        onChange={(e) =>
                          handleOtherFieldChange(
                            e.target.value.charAt(0).toUpperCase() +
                              e.target.value.slice(1),
                            field.id,
                            "label"
                          )
                        }
                        placeholder="Enter title"
                        className="w-30"
                      />
                      <Input
                        value={field.value}
                        onChange={(e) =>
                          handleOtherFieldChange(
                            e.target.value,
                            field.id,
                            "value"
                          )
                        }
                        className="w-30"
                        placeholder="Enter amount"
                        type="number"
                        min={0}
                        onWheel={(e) => e.target.blur()}
                      />
                      <Button
                        type="danger"
                        onClick={() => removeOtherField(field.id)}
                      >
                        Remove
                      </Button>
                    </div>
                  </div>
                ))}

                {templateType === "tution" && (
                  <div className="pb-4">
                    <Button
                      type="primary"
                      htmlType="button"
                      onClick={addOtherField}
                      className="mb-2"
                    >
                      Add
                    </Button>
                  </div>
                )}
                {templateType === "tution" && isManualMode && (
                  <div className="mb-4 mt-2 border border-gray-200 rounded-md p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h6 className="font-semibold text-gray-700 m-0">
                        Fee Categories{" "}
                        <span className="font-normal text-gray-400 text-xs">(optional — for multiple fee tiers)</span>
                      </h6>
                      <Button
                        size="small"
                        type="dashed"
                        onClick={() =>
                          setCategories([
                            ...categories,
                            { name: `Category ${categories.length + 1}`, tuition_amount: "" },
                          ])
                        }
                      >
                        + Add Category
                      </Button>
                    </div>
                    {categories.length === 0 && (
                      <p className="text-gray-400 text-sm m-0">
                        No categories added. Add categories to offer different tuition tiers within this template.
                      </p>
                    )}
                    {categories.map((cat, idx) => (
                      <div key={idx} className="flex items-center gap-3 mb-2">
                        <Input
                          placeholder="Category name"
                          value={cat.name}
                          onChange={(e) => {
                            const updated = [...categories];
                            updated[idx] = { ...updated[idx], name: e.target.value };
                            setCategories(updated);
                          }}
                          style={{ flex: 2 }}
                        />
                        <Input
                          placeholder="Tuition amount (₹)"
                          type="number"
                          min={0}
                          value={cat.tuition_amount}
                          onChange={(e) => {
                            const updated = [...categories];
                            updated[idx] = { ...updated[idx], tuition_amount: e.target.value };
                            setCategories(updated);
                          }}
                          onWheel={(e) => e.target.blur()}
                          style={{ flex: 1 }}
                        />
                        <Button
                          danger
                          size="small"
                          onClick={() => setCategories(categories.filter((_, i) => i !== idx))}
                        >
                          Remove
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                {templateType !== "transportation" && (
                  <Form.Item name="total" label="Total Amount" className="w-30">
                    <Input readOnly />
                  </Form.Item>
                )}
                <Form.Item label="Academic Year" className="w-30">
                  <Input
                    readOnly
                    value={
                      templateType === "transportation"
                        ? selectedYear
                        : selectedYearr
                          ? `${selectedYearr}-${String(selectedYearr + 1).slice(-2)}`
                          : "—"
                    }
                    style={{ background: "#EBEBEB", cursor: "default" }}
                  />
                </Form.Item>
                <Form.Item
                  name="due_date"
                  label="Due Date"
                  className="w-30"
                  rules={[
                    {
                      required: true,
                      message: "Due Date is required",
                    },
                  ]}
                >
                  <DatePicker
                    format="DD-MM-YYYY"
                    getPopupContainer={(trigger) => trigger.parentNode}
                    className="w-full"
                    disabledDate={disabledDate}
                  />
                </Form.Item>
                <Form.Item label="Payment Mode" className="w-30">
                  <Radio.Group
                    value={paymentMode}
                    onChange={(e) => setPaymentMode(e.target.value)}
                    optionType="button"
                    buttonStyle="solid"
                    size="small"
                  >
                    <Radio.Button value="monthly">Monthly</Radio.Button>
                    <Radio.Button value="quarterly">Quarterly</Radio.Button>
                    <Radio.Button value="termly">Termly</Radio.Button>
                    <Radio.Button value="yearly">Yearly</Radio.Button>
                  </Radio.Group>
                  {paymentMode !== "termly" && (
                    <div style={{ marginTop: 6, fontSize: 12, color: "#1890ff" }}>
                      {paymentModeLabel(
                        paymentMode,
                        form.getFieldValue("academic_start_date"),
                        form.getFieldValue("academic_end_date")
                      )}
                    </div>
                  )}
                </Form.Item>
                {paymentMode === "termly" && (
                  <Form.Item
                    name="installment_count"
                    label="Number of terms (Max 12)"
                    className="w-30"
                    rules={[
                      { required: true, message: "Number of terms is required" },
                      {
                        validator: (_, value) => {
                          if (value && (value < 1 || value > 12)) {
                            return Promise.reject(
                              "Number of terms should be between 1 and 12"
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <Input
                      type="number"
                      placeholder="Enter number of terms"
                      onKeyDown={(e) => {
                        if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                          e.preventDefault();
                        }
                      }}
                      onWheel={(e) => e.target.blur()}
                    />
                  </Form.Item>
                )}
                <div className="flex justify-center items-center py-5">
                  <div className="inline-flex items-center space-x-2">
                    <Button
                      size="large"
                      type="primary"
                      htmlType="submit"
                      className="text-white"
                    >
                      Save
                    </Button>
                  </div>
                </div>
              </Form>
            </div>
            {templateType !== "transportation" && (
              <FeeTable
                postTemplateResponse={postTemplateResponse}
                templateType={templateType}
              />
            )}
          </div>
        </Modal>

        <TransportBillingZoneManager
          visible={zoneManagerVisible}
          onClose={() => {
            setZoneManagerVisible(false);
            queryClient.invalidateQueries(["transport-billing-zones"]);
          }}
        />
      </div>
    </>
  );
};

export default FeeTemplate;
